import { useState, useEffect } from 'react';
import { useAccount, useReadContract } from 'wagmi';
import { formatUnits } from 'viem';
import { CONTRACT_ADDRESSES, FACTORY_ABI, PAIR_ABI, FTSO_REGISTRY_ABI, ROUTER_ABI } from '../config/contracts';
import { BACKEND_URL } from '../config/api';
import { getTokenLogo } from '../config/tokens';

function PoolsOverview({ isConnected, onNavigateToLiquidity }) {
  const { address } = useAccount();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('tvl'); // 'tvl', 'volume', 'apr'

  // Fetch pools from backend
  useEffect(() => {
    const fetchPools = async () => {
      try {
        setError(null);
        const response = await fetch(`${BACKEND_URL}/api/liquidity-pools/list`);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(errorData.detail || `HTTP ${response.status}`);
        }

        const data = await response.json();
        setPools(data.pools || []);
      } catch (err) {
        console.error('Error fetching pools:', err);
        setError(err.message || 'Failed to load pool data');
        setPools([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPools();
  }, []);

  // Get FLR/USD price from FTSO
  const { data: flrPriceData } = useReadContract({
    address: CONTRACT_ADDRESSES.FTSO_REGISTRY,
    abi: FTSO_REGISTRY_ABI,
    functionName: 'getCurrentPriceWithDecimals',
    args: ['FLR'],
    query: {
      enabled: !!CONTRACT_ADDRESSES.FTSO_REGISTRY,
      retry: false,
      throwOnError: false,
    },
  });

  const flrPriceUSD = flrPriceData && flrPriceData[0] && flrPriceData[2]
    ? Number(formatUnits(flrPriceData[0], Number(flrPriceData[2])))
    : 0.025; // Fallback price

  // Calculate APR from trading fees
  const calculateAPR = (pool) => {
    if (!pool.tvl || !pool.volume24h || pool.tvl === 0) return 0;
    const FEE_RATE = 0.003; // 0.3% trading fee (standard Uniswap-style)
    const dailyFees = pool.volume24h * FEE_RATE;
    const annualFees = dailyFees * 365;
    const apr = (annualFees / pool.tvl) * 100;
    return apr;
  };

  // Filter and sort pools
  const filteredPools = pools
    .filter(pool =>
      pool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pool.token0.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pool.token1.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'tvl') return (b.tvl || 0) - (a.tvl || 0);
      if (sortBy === 'volume') return (b.volume24h || 0) - (a.volume24h || 0);
      if (sortBy === 'apr') return calculateAPR(b) - calculateAPR(a);
      return 0;
    });

  const formatCurrency = (value) => {
    if (value >= 0.01) return `$${value.toFixed(2)}`;
    if (value >= 0.0001) return `$${value.toFixed(4)}`;
    if (value > 0) return `<$0.0001`;
    return `$0.00`;
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px 15px',
  };

  const headerStyle = {
    marginBottom: '30px',
  };

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#C4B5FD',
    marginBottom: '8px',
    textAlign: 'left',
  };

  const subtitleStyle = {
    fontSize: '14px',
    color: '#7C5BB0',
    textAlign: 'left',
  };

  const controlsStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  };

  const searchStyle = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
  };

  const sortButtonsStyle = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  };

  const sortButtonStyle = (active) => ({
    flex: '1',
    minWidth: '80px',
    padding: '8px 12px',
    backgroundColor: active ? '#8B5CF6' : '#2a2a2a',
    color: active ? '#fff' : '#9ca3af',
    border: `1px solid ${active ? '#8B5CF6' : '#3a3a3a'}`,
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  const statsBarStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
    marginBottom: '24px',
  };

  const statCardStyle = {
    backgroundColor: '#1a1a1a',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    padding: '16px',
    textAlign: 'center',
  };

  const statLabelStyle = {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '6px',
  };

  const statValueStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: '#C4B5FD',
  };

  const poolsGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '16px',
  };

  const poolCardStyle = {
    backgroundColor: 'rgba(15, 8, 30, 0.92)',
    border: '1px solid rgba(139, 92, 246, 0.35)',
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 0 14px rgba(139, 92, 246, 0.2)',
  };

  const poolHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  };

  const poolNameStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const tokenIconsStyle = {
    display: 'flex',
    marginLeft: '-8px',
  };

  const tokenIconStyle = (index) => ({
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: '2px solid #0a0514',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#fff',
    marginLeft: index > 0 ? '-8px' : '0',
    zIndex: 10 - index,
    backgroundColor: '#1a1a1a',
    overflow: 'hidden',
  });

  const tokenImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const poolTitleStyle = {
    fontSize: '18px',
    fontWeight: '700',
    color: '#fff',
  };

  const aprBadgeStyle = {
    backgroundColor: 'rgba(74, 222, 128, 0.15)',
    color: '#4ade80',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '700',
  };

  const poolStatsStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  };

  const poolStatStyle = {
    display: 'flex',
    flexDirection: 'column',
  };

  const poolStatLabelStyle = {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '4px',
  };

  const poolStatValueStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
  };

  const emptyIconStyle = {
    fontSize: '48px',
    marginBottom: '16px',
  };

  const totalTVL = pools.reduce((sum, pool) => sum + (pool.tvl || 0), 0);
  const totalVolume = pools.reduce((sum, pool) => sum + (pool.volume24h || 0), 0);

  return (
    <div className="content-section active">
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Liquidity Pools</h1>
          <p style={subtitleStyle}>Provide liquidity and earn on trading fees</p>
        </div>

        {/* Stats Overview */}
        <div style={statsBarStyle}>
          <div style={statCardStyle}>
            <div style={statLabelStyle}>Total TVL</div>
            <div style={statValueStyle}>{formatCurrency(totalTVL)}</div>
          </div>
          <div style={statCardStyle}>
            <div style={statLabelStyle}>24h Volume</div>
            <div style={statValueStyle}>{formatCurrency(totalVolume)}</div>
          </div>
        </div>

        {/* Controls */}
        <div style={controlsStyle}>
          <input
            type="text"
            placeholder="Search pools by name or token..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchStyle}
          />
          <div style={sortButtonsStyle}>
            <button
              onClick={() => setSortBy('apr')}
              style={sortButtonStyle(sortBy === 'apr')}
            >
              APR
            </button>
            <button
              onClick={() => setSortBy('tvl')}
              style={sortButtonStyle(sortBy === 'tvl')}
            >
              TVL
            </button>
            <button
              onClick={() => setSortBy('volume')}
              style={sortButtonStyle(sortBy === 'volume')}
            >
              Volume
            </button>
          </div>
        </div>

        {/* Pools Grid */}
        {loading ? (
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>⏳</div>
            <div>Loading pools...</div>
          </div>
        ) : error ? (
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>⚠️</div>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#ef4444' }}>
              Failed to Load Pool Data
            </div>
            <div style={{ fontSize: '14px', marginBottom: '16px' }}>
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#8B5CF6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : filteredPools.length === 0 ? (
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>💧</div>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
              No pools found
            </div>
            <div style={{ fontSize: '14px' }}>
              {searchTerm ? 'Try adjusting your search' : 'Be the first to create a pool!'}
            </div>
          </div>
        ) : (
          <div style={poolsGridStyle}>
            {filteredPools.map((pool) => (
              <div
                key={pool.id || pool.pairAddress}
                style={poolCardStyle}
                onClick={() => onNavigateToLiquidity && onNavigateToLiquidity(pool)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.6)';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.35)';
                  e.currentTarget.style.boxShadow = '0 0 14px rgba(139, 92, 246, 0.2)';
                }}
              >
                <div style={poolHeaderStyle}>
                  <div style={poolNameStyle}>
                    <div style={tokenIconsStyle}>
                      <div style={tokenIconStyle(0)}>
                        {getTokenLogo(pool.token0) ? (
                          <img
                            src={getTokenLogo(pool.token0)}
                            alt={pool.token0}
                            style={tokenImageStyle}
                          />
                        ) : (
                          pool.token0.charAt(0)
                        )}
                      </div>
                      <div style={tokenIconStyle(1)}>
                        {getTokenLogo(pool.token1) ? (
                          <img
                            src={getTokenLogo(pool.token1)}
                            alt={pool.token1}
                            style={tokenImageStyle}
                          />
                        ) : (
                          pool.token1.charAt(0)
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={poolTitleStyle}>{pool.name}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                        {pool.token0}/{pool.token1}
                      </div>
                    </div>
                  </div>
                  <div style={aprBadgeStyle}>
                    {calculateAPR(pool).toFixed(2)}% APR
                  </div>
                </div>

                <div style={poolStatsStyle}>
                  <div style={poolStatStyle}>
                    <div style={poolStatLabelStyle}>TVL</div>
                    <div style={poolStatValueStyle}>
                      {pool.tvl ? formatCurrency(pool.tvl) : '—'}
                    </div>
                  </div>
                  <div style={poolStatStyle}>
                    <div style={poolStatLabelStyle}>24h Volume</div>
                    <div style={poolStatValueStyle}>
                      {pool.volume24h ? formatCurrency(pool.volume24h) : '—'}
                    </div>
                  </div>
                  {isConnected && pool.lpStaked > 0 && (
                    <>
                      <div style={poolStatStyle}>
                        <div style={poolStatLabelStyle}>Your LP Staked</div>
                        <div style={{ ...poolStatValueStyle, color: '#A78BFA' }}>
                          {pool.lpStaked.toFixed(4)}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {isConnected && pool.lpBalance > 0 && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(139, 92, 246, 0.15)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#A78BFA',
                  }}>
                    Your Position: {pool.lpBalance.toFixed(4)} LP
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PoolsOverview;
